// Quote Builder — Drawer Pack configurator.
//
// Separated from the cabinet picker on 2026-05-26 (Steve). Drawer packs
// have a different set of relevant options to cabinets — no hinge side,
// no drawerline toggle, no tall-unit gable reduction or top/bottom door
// fields — so they get their own focused module. The data plumbing
// (DTN finishes + virtual cab pricing) is unchanged; a DTN catalogue
// update to follow in a separate session will replace the underlying
// data source without touching this picker's shape.
//
// Wires to:
//   • GET /api/dtn/finishes         — full board-group list (shared with cabinet picker)
//   • The session-defaults pubsub   — auto-seeds spec.finish on cab select
//   • The selected-cab pubsub       — re-mounts when a new drawer pack is clicked
//
// Mount API: mountDrawerPackPicker(host, store, cab).

import { alertDialog } from "@tkos/shared-ui";
import type {
  DrawerPackConfig,
  DtnFinishRow,
  QuoteItem,
  SessionFinish,
  W8Cab,
  W8VirtualCab,
} from "./types";
import type { QuoteStore } from "./state";
import { escapeHtml, fmtGbp, newId } from "./formatters";
import { recalcItem } from "./pricing";
import {
  getSessionFinish,
  loadFinishesFor,
  subscribeSessionFinish,
} from "./session-defaults";
import {
  setSelectedCab,
  subscribeSelectedCab,
} from "./picker-selection";
import {
  DTN_KEY,
  DISC_LABEL,
  cabDepth,
  cabHeight,
  isVirtualCab,
  sessionFinishToDtnRow,
} from "./cabinet-picker";

// Drawer box brand options — verbatim from the legacy cabinet picker so
// the existing four brands continue to work until the DTN catalogue
// update provides a richer set.
const DRAWER_BOX_BRANDS = ["Antaro", "Legrabox", "Elite/Meta", "MFC Pure"] as const;

// Drawer configuration options — same labels the cabinet picker used.
// The label strings double as the value, so existing quote items written
// before the split parse identically.
const DRAWER_CONFIG_OPTIONS = [
  "2 x Pan drawers",
  "2 x Pan drawers + internal cutlery & fascia",
  "1 x Cutlery + 2 Pan drawers",
  "2 x Cutlery + 1 Pan drawer",
  "3 x Cutlery + 1 Pan drawer",
  "4 x Medium drawers (K height)",
  "5 x Cutlery drawers (M height)",
] as const;

export interface DrawerPackPickerHandle {
  unmount(): void;
}

interface DpSpec {
  width: number | "";
  height: number | "";
  depth: number | "";
  qty: number;
  zoneId: string;
  finish: DtnFinishRow | null;
  finish_pg: 1 | 2 | 3 | 4 | 5;
  drawer_config: string;
  drawer_box: string;
  handleless: boolean;
  edging: string;
  notes: string;
}

interface DpState {
  cab: W8Cab;
  spec: DpSpec;
  finishes: DtnFinishRow[];
  finishesLoading: boolean;
}

function parseWidthFromDesc(desc: string): number {
  const m = desc.match(/\b(\d{3,4})\b/);
  return m ? Number(m[1]) : 0;
}

function blankSpec(cab: W8Cab, sessionFinish: SessionFinish | null, defaultZoneId: string): DpSpec {
  const w = isVirtualCab(cab) ? cab.width_mm : parseWidthFromDesc(cab.desc);
  // Pre-populate drawer config from the virtual cab's bundled DP config when
  // the user picked a DRAWER_PACK_CONFIGS row from the left pane. The
  // _dpConfig.desc happens to match one of DRAWER_CONFIG_OPTIONS for the
  // common entries; when it doesn't we leave the field blank so the user
  // can choose explicitly.
  let preConfig = "";
  if (isVirtualCab(cab)) {
    const dp: DrawerPackConfig = cab._dpConfig;
    const match = DRAWER_CONFIG_OPTIONS.find((o) => o.toLowerCase() === dp.desc.toLowerCase());
    if (match) preConfig = match;
  }
  return {
    width: w || "",
    height: cabHeight(cab) || "",
    depth: cabDepth(cab) || "",
    qty: 1,
    zoneId: defaultZoneId,
    finish: sessionFinish ? sessionFinishToDtnRow(sessionFinish) : null,
    finish_pg: sessionFinish?.pg ?? 5,
    drawer_config: preConfig,
    drawer_box: "",
    handleless: false,
    edging: "",
    notes: "",
  };
}

export function mountDrawerPackPicker(
  host: HTMLElement,
  store: QuoteStore,
  cab: W8Cab,
): DrawerPackPickerHandle {
  const initial = store.get();
  const firstZoneId = initial.zones[0]?.id ?? "";
  const seedFinish = getSessionFinish(DTN_KEY);
  const state: DpState = {
    cab,
    spec: blankSpec(cab, seedFinish, firstZoneId),
    finishes: [],
    finishesLoading: false,
  };

  const unsubSession = subscribeSessionFinish((supplierKey, finish) => {
    if (supplierKey !== DTN_KEY) return;
    if (!finish) return;
    state.spec.finish = sessionFinishToDtnRow(finish);
    state.spec.finish_pg = finish.pg;
    paint();
  });

  const unsubCab = subscribeSelectedCab((next) => {
    if (!next) return;
    state.cab = next;
    state.spec = blankSpec(next, getSessionFinish(DTN_KEY), state.spec.zoneId || firstZoneId);
    paint();
  });

  void loadDtnFinishes();

  async function loadDtnFinishes(): Promise<void> {
    if (state.finishesLoading || state.finishes.length > 0) return;
    state.finishesLoading = true;
    try {
      const list = await loadFinishesFor(DTN_KEY);
      const res = await fetch("/api/dtn/finishes?limit=1000", { credentials: "include" });
      if (res.ok) {
        const data = await res.json() as { results?: DtnFinishRow[] };
        if (Array.isArray(data.results)) state.finishes = data.results;
      }
      void list;
    } catch { /* swallow */ }
    state.finishesLoading = false;
    if (state.spec.finish && state.finishes.length > 0) {
      const real = state.finishes.find((f) => f.code === state.spec.finish?.code);
      if (real) {
        state.spec.finish = real;
        state.spec.finish_pg = real.pg;
      }
    }
    paint();
  }

  function paint(): void {
    host.innerHTML = render(state, store);
    wire();
  }

  function wire(): void {
    const $ = <T extends HTMLElement>(sel: string): T | null => host.querySelector<T>(sel);
    const $$ = (sel: string): HTMLElement[] => Array.from(host.querySelectorAll<HTMLElement>(sel));

    $<HTMLSelectElement>("[data-role='zone']")?.addEventListener("change", (e) => {
      state.spec.zoneId = (e.target as HTMLSelectElement).value;
    });
    $<HTMLInputElement>("[data-role='qty']")?.addEventListener("input", (e) => {
      state.spec.qty = Number((e.target as HTMLInputElement).value) || 1;
      paint();
    });
    $<HTMLInputElement>("[data-role='width']")?.addEventListener("input", (e) => {
      const v = Number((e.target as HTMLInputElement).value);
      state.spec.width = Number.isFinite(v) && v > 0 ? v : "";
    });
    $<HTMLInputElement>("[data-role='height']")?.addEventListener("input", (e) => {
      const v = Number((e.target as HTMLInputElement).value);
      state.spec.height = Number.isFinite(v) && v > 0 ? v : "";
    });
    $<HTMLInputElement>("[data-role='depth']")?.addEventListener("input", (e) => {
      const v = Number((e.target as HTMLInputElement).value);
      state.spec.depth = Number.isFinite(v) && v > 0 ? v : "";
    });
    $<HTMLSelectElement>("[data-role='drawer-config']")?.addEventListener("change", (e) => {
      state.spec.drawer_config = (e.target as HTMLSelectElement).value;
    });
    $<HTMLSelectElement>("[data-role='drawer-box']")?.addEventListener("change", (e) => {
      state.spec.drawer_box = (e.target as HTMLSelectElement).value;
    });
    $$("[data-role='handleless']").forEach((btn) => {
      btn.addEventListener("click", () => {
        state.spec.handleless = btn.dataset["handleless"] === "true";
        paint();
      });
    });
    $<HTMLSelectElement>("[data-role='finish']")?.addEventListener("change", (e) => {
      const key = (e.target as HTMLSelectElement).value;
      if (!key) {
        state.spec.finish = null;
        state.spec.finish_pg = 5;
      } else {
        const f = state.finishes.find((x) => `${x.brand}|${x.code}|${x.structure}` === key);
        if (f) {
          state.spec.finish = f;
          state.spec.finish_pg = f.pg;
        }
      }
      paint();
    });
    $<HTMLInputElement>("[data-role='edging']")?.addEventListener("input", (e) => {
      state.spec.edging = (e.target as HTMLInputElement).value;
    });
    $<HTMLInputElement>("[data-role='notes']")?.addEventListener("input", (e) => {
      state.spec.notes = (e.target as HTMLInputElement).value;
    });
    $<HTMLButtonElement>("[data-role='cancel']")?.addEventListener("click", () => {
      setSelectedCab(null);
    });
    $<HTMLButtonElement>("[data-role='add']")?.addEventListener("click", () => {
      addToQuote();
    });
  }

  function addToQuote(): void {
    const cab = state.cab;
    const s = state.spec;

    const missing: string[] = [];
    if (!s.width || Number(s.width) <= 0) missing.push("Width");
    if (!s.drawer_config) missing.push("Drawer configuration");
    if (!s.drawer_box) missing.push("Drawer box brand");
    if (!s.finish) missing.push("Board finish");
    if (missing.length) {
      void alertDialog(`Missing: ${missing.join(", ")}`, { title: "Missing fields" });
      return;
    }

    const pg = s.finish_pg || 5;
    const cabPrices: Record<string, number> = {
      pg1: cab.pg1, pg2: cab.pg2, pg3: cab.pg3, pg4: cab.pg4, pg5: cab.pg5,
    };
    const carcassPrice = cabPrices[`pg${pg}`] ?? cab.pg5 ?? 0;
    const f = s.finish!;

    const noteParts: string[] = [];
    if (s.handleless) noteParts.push("TRUE HANDLELESS");
    const finishLabel = [f.brand, f.name, f.code ? `(${f.code} ${f.structure})` : ""].filter(Boolean).join(" ").trim();
    noteParts.push(`Finish: ${finishLabel} — PG${pg}`);
    if (s.edging) noteParts.push(`Front edging: ${s.edging}`);
    noteParts.push(`Drawers: ${s.drawer_config}`);
    noteParts.push(`Box: ${s.drawer_box}`);
    if (s.notes) noteParts.push(s.notes);

    const virtual = isVirtualCab(cab) ? cab as W8VirtualCab : null;
    const cabCode = virtual?._carcassCode ?? cab.code;
    const dpSuffix = virtual ? " (complete with drawers)" : "";

    const item: QuoteItem = {
      id: newId("i"),
      category: "cabinets",
      supplier: "Trade Kitchens by DTN",
      code: cabCode,
      desc: `${cab.desc} [PG${pg}]${dpSuffix}`,
      qty: s.qty || 1,
      width: Number(s.width) || (virtual ? virtual.width_mm : 0),
      height: Number(s.height) || cabHeight(cab),
      depth: Number(s.depth) || cabDepth(cab),
      notes: noteParts.join(" | "),
      // Same listPrice convention as the cabinet picker: store the full
      // PG5 list with explicit per-PG discPct so the saving is visible.
      listPrice: cab.pg5 || carcassPrice,
      discPct: f.disc || 0,
      tradePrice: carcassPrice,
      markupMode: "pct",
      markupPct: store.get().defaults.markupPct ?? 0,
      markupGbp: 0,
      retailPrice: 0,
      unitTypeId: cab.unit_type,
      unitTypeLabel: cab.unit_label || "Drawer Packs",
      carcassFinish: `${f.brand} ${f.name}`.trim(),
      carcassPriceGroup: pg,
      edging: s.edging || "",
      hinging: "N",
      construction: s.handleless ? "true-handleless" : "standard",
      drawerConfig: s.drawer_config,
      drawerBox: s.drawer_box,
    };
    recalcItem(item, null);

    const zoneId = s.zoneId || store.get().zones[0]?.id || "";
    store.set((d) => {
      const z = d.zones.find((zo) => zo.id === zoneId) ?? d.zones[0];
      if (z) z.items.push(item);
    });

    // Reset qty to 1 so the next add doesn't inherit the previous quantity.
    state.spec.qty = 1;
    setSelectedCab(null);
  }

  paint();

  return {
    unmount(): void {
      unsubSession();
      unsubCab();
      host.innerHTML = "";
    },
  };
}

// ── Render ───────────────────────────────────────────────────────────────

function render(state: DpState, store: QuoteStore): string {
  const { cab, spec } = state;
  const pg = spec.finish_pg || 5;
  const cabPrices: Record<string, number> = {
    pg1: cab.pg1, pg2: cab.pg2, pg3: cab.pg3, pg4: cab.pg4, pg5: cab.pg5,
  };
  const price = cabPrices[`pg${pg}`] ?? cab.pg5 ?? 0;
  const q = store.get();
  const zoneOpts = q.zones.map(
    (z) => `<option value="${escapeHtml(z.id)}"${z.id === spec.zoneId ? " selected" : ""}>${escapeHtml(z.name)}</option>`,
  ).join("") || `<option value="">— No zone —</option>`;

  const dcOpts = ["", ...DRAWER_CONFIG_OPTIONS]
    .map((o) => `<option value="${escapeHtml(o)}"${spec.drawer_config === o ? " selected" : ""}>${o || "— select —"}</option>`)
    .join("");
  const dbOpts = ["", ...DRAWER_BOX_BRANDS]
    .map((o) => `<option value="${escapeHtml(o)}"${spec.drawer_box === o ? " selected" : ""}>${o || "— select —"}</option>`)
    .join("");

  let html = `<div class="qb-w8-spec">
    <div class="qb-w8-title"><span class="sup">DTN</span>${escapeHtml(cab.desc)}</div>
    <div class="qb-w8-subtitle">${escapeHtml(cab.code)} · Drawer Pack${cab.section ? ` · ${escapeHtml(cab.section)}` : ""}</div>
    <div class="qb-w8-summary">
      <div><div class="lbl">Unit price (PG${pg})</div><div class="val">${fmtGbp(price)}</div></div>
      <div class="right"><span class="qb-w8-summary-mult">× ${spec.qty}</span><span class="qb-w8-summary-total">${fmtGbp(price * spec.qty)}</span></div>
    </div>
    <div class="qb-w8-row two">
      <div><label class="qb-w8-lbl">Zone</label><select class="qb-w8-inp" data-role="zone">${zoneOpts}</select></div>
      <div><label class="qb-w8-lbl">Qty</label><input class="qb-w8-inp" type="number" min="1" value="${spec.qty}" data-role="qty"></div>
    </div>
    <div class="qb-w8-row three">
      <div><label class="qb-w8-lbl">Width (mm)<span class="req">*</span></label><input class="qb-w8-inp" type="number" value="${escapeHtml(String(spec.width))}" data-role="width"></div>
      <div><label class="qb-w8-lbl">Height (mm)</label><input class="qb-w8-inp" type="number" value="${escapeHtml(String(spec.height))}" data-role="height"></div>
      <div><label class="qb-w8-lbl">Depth (mm)</label><input class="qb-w8-inp" type="number" value="${escapeHtml(String(spec.depth))}" data-role="depth"></div>
    </div>
    <div class="qb-w8-row two">
      <div><label class="qb-w8-lbl">Drawer configuration<span class="req">*</span></label><select class="qb-w8-inp" data-role="drawer-config">${dcOpts}</select></div>
      <div><label class="qb-w8-lbl">Drawer box brand<span class="req">*</span></label><select class="qb-w8-inp" data-role="drawer-box">${dbOpts}</select></div>
    </div>
    <div class="qb-w8-row two">
      <div><label class="qb-w8-lbl">True Handleless?</label><div class="qb-w8-toggle">
        <button type="button" class="${!spec.handleless ? "active" : ""}" data-role="handleless" data-handleless="false">No</button>
        <button type="button" class="${spec.handleless ? "active" : ""}" data-role="handleless" data-handleless="true">Yes</button>
      </div></div>
      <div></div>
    </div>
    <div class="qb-w8-finish-block">${renderFinishSelector(state)}</div>
    <div class="qb-w8-row two">
      <div><label class="qb-w8-lbl">Front edging colour</label><input class="qb-w8-inp" type="text" value="${escapeHtml(spec.edging)}" data-role="edging"></div>
      <div><label class="qb-w8-lbl">Notes</label><input class="qb-w8-inp" type="text" value="${escapeHtml(spec.notes)}" data-role="notes"></div>
    </div>`;

  const badge = spec.finish
    ? `<span class="qb-w8-pgbadge">PG${pg} — ${spec.finish.disc}% off list</span>`
    : `<em class="qb-w8-hint-em">Select a finish to set price group</em>`;
  html += `<div class="qb-w8-foot">
    <div class="qb-w8-hint">${badge}</div>
    <div class="qb-w8-foot-actions">
      <button type="button" class="qb-btn qb-btn--ghost" data-role="cancel">Cancel</button>
      <button type="button" class="qb-btn qb-btn--primary" data-role="add">Add to quote</button>
    </div>
  </div></div>`;
  return html;
}

function renderFinishSelector(state: DpState): string {
  const { spec, finishes, finishesLoading } = state;
  if (!finishes.length) {
    const msg = finishesLoading ? "Loading finishes…" : "No finishes loaded";
    return `<label class="qb-w8-lbl">Board finish<span class="req">*</span></label><div class="qb-w8-finish-loading">${msg}</div>`;
  }
  const f = spec.finish;
  const headerBadge = f && f.brand
    ? ` <span class="qb-w8-pgbadge">${escapeHtml(f.brand)} · PG${f.pg}</span>`
    : "";
  let html = `<label class="qb-w8-lbl">Board finish<span class="req">*</span>${headerBadge}</label>
    <select class="qb-w8-inp" data-role="finish">
      <option value="">— Select board finish (sets price group) —</option>`;
  for (const pg of [1, 2, 3, 4, 5] as const) {
    const arr = finishes.filter((x) => x.pg === pg);
    if (!arr.length) continue;
    html += `<optgroup label="PG${pg} — ${DISC_LABEL[pg]}">`;
    for (const fn of arr) {
      const key = `${fn.brand}|${fn.code}|${fn.structure}`;
      const selected = f && `${f.brand}|${f.code}|${f.structure}` === key ? " selected" : "";
      html += `<option value="${escapeHtml(key)}"${selected}>${escapeHtml(fn.brand)} · ${escapeHtml(fn.name)} (${escapeHtml(fn.code)} ${escapeHtml(fn.structure)})</option>`;
    }
    html += `</optgroup>`;
  }
  html += `</select>`;
  return html;
}
